import { useState } from 'react';
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@material-ui/core';

import { useStoreActions } from '~/store/hooks';
import { isoToFormat } from '~/utils/date';
import { extractErrorMessage } from '~/utils/error';
import { createValues } from './form';
import Button from '~/ui/components/common/Button';
import NoDataFound from '~/ui/components/reusable/NoDataFound';
import HospitalizationModal from '../popups/HospitalizationModal';
import Item from './Item';

import { ICarePlanHospitalization } from '~/services/api/carePlanHospitalizations/types';
import { IFormValues } from '../popups/HospitalizationModal/form/types';
import { IPagePermissions } from '../../types';
import styles from './Hospitalizations.module.scss';

interface IProps {
  patientId: number;
  items: ICarePlanHospitalization[];
  permissions: IPagePermissions;
  onCarePlanChange: () => void;
}

const Hospitalizations = ({ patientId, items, permissions, onCarePlanChange }: IProps) => {
  const [showCreate, setShowCreate] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const { onCreateCarePlanHospitalization } = useStoreActions(
    actions => actions.carePlanHospitalizations,
  );

  const { showError } = useStoreActions(actions => actions.snackbar);

  const handleCreateHospitalization = async (values: IFormValues) => {
    const payload = {
      ...values,
      date: isoToFormat(values.date, 'y-MM-dd'),
    } as ICarePlanHospitalization;

    try {
      setIsCreating(true);
      await onCreateCarePlanHospitalization({ patientId, data: payload });
      onCarePlanChange();
      setShowCreate(false);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerWrapper}>
        <div className={styles.title}>Hospitalizations</div>
        <div className={styles.actions}>
          {permissions.canManage && (
            <Button variant="contained" color="primary" onClick={() => setShowCreate(true)}>
              + Add New
            </Button>
          )}
        </div>
      </div>
      <div className={styles.tableContainer}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="20%">Date</TableCell>
                <TableCell>Reason</TableCell>
                {permissions.canManage && <TableCell width={120} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length ? (
                items.map(item => (
                  <Item
                    key={item.id}
                    item={item}
                    permissions={permissions}
                    onCarePlanChange={onCarePlanChange}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3}>
                    <NoDataFound />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      {showCreate && (
        <HospitalizationModal
          initialValues={createValues}
          isProcessing={isCreating}
          onSubmit={handleCreateHospitalization}
          onClose={() => setShowCreate(false)}
        />
      )}
    </div>
  );
};

export default Hospitalizations;
